import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Switch } from '@/components/ui/switch';
import { Label } from '@/components/ui/label';
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from '@/components/ui/alert-dialog';
import { Download, Trash2, Shield, FileText, Cookie, AlertTriangle } from 'lucide-react';
import { Textarea } from '@/components/ui/textarea';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const GDPRSettings = () => {
  const [consents, setConsents] = useState({});
  const [loading, setLoading] = useState(true);
  const [showDeleteDialog, setShowDeleteDialog] = useState(false);
  const [deleteReason, setDeleteReason] = useState('');
  const [exporting, setExporting] = useState(false);

  useEffect(() => {
    loadConsents();
  }, []);

  const loadConsents = async () => {
    try {
      const response = await axios.get(`${API}/gdpr/consents`, {
        withCredentials: true
      });
      
      const consentMap = {};
      response.data.consents.forEach(consent => {
        consentMap[consent.consent_type] = consent.granted;
      });
      
      setConsents(consentMap);
      setLoading(false);
    } catch (error) {
      console.error('Error loading consents:', error);
      setLoading(false);
    }
  };

  const updateConsent = async (consentType, granted, consentText) => {
    try {
      await axios.post(`${API}/gdpr/update-consent`, {
        consent_type: consentType,
        granted: granted,
        consent_text: consentText
      }, { withCredentials: true });
      
      setConsents(prev => ({ ...prev, [consentType]: granted }));
      toast.success(`Consent ${granted ? 'granted' : 'withdrawn'} successfully`);
    } catch (error) {
      toast.error('Failed to update consent');
    }
  };

  const handleExportData = async () => {
    setExporting(true);
    try {
      const response = await axios.post(`${API}/gdpr/export-data`, {}, {
        withCredentials: true
      });
      
      // Create downloadable file
      const dataStr = JSON.stringify(response.data.data, null, 2);
      const dataBlob = new Blob([dataStr], { type: 'application/json' });
      const url = URL.createObjectURL(dataBlob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `my-data-${new Date().toISOString()}.json`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(url);
      
      toast.success('Data exported successfully');
    } catch (error) {
      toast.error('Failed to export data');
    } finally {
      setExporting(false);
    }
  };

  const handleRequestDeletion = async () => {
    try {
      const response = await axios.post(`${API}/gdpr/request-deletion`, {
        reason: deleteReason
      }, { withCredentials: true });
      
      toast.success(
        `Account deletion requested. Your account will be permanently deleted on ${new Date(response.data.deletion_scheduled_for).toLocaleDateString()}`
      );
      
      setShowDeleteDialog(false);
      setDeleteReason('');
      
      // Logout after 3 seconds
      setTimeout(() => {
        window.location.href = '/login';
      }, 3000);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to request deletion');
    }
  };

  if (loading) {
    return <div className="p-6">Loading GDPR settings...</div>;
  }

  return (
    <div className="p-6 space-y-6">
      <div>
        <h1 className="text-3xl font-bold" style={{fontFamily: 'Space Grotesk, sans-serif'}}>GDPR & Privacy Settings</h1>
        <p className="text-slate-600 mt-2">Manage your data and privacy preferences</p>
      </div>

      {/* Data Rights */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Shield className="w-5 h-5" />
            Your Data Rights
          </CardTitle>
          <CardDescription>
            Under GDPR, you have rights regarding your personal data
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="flex items-center justify-between p-4 border rounded-lg">
            <div className="flex items-center gap-3">
              <Download className="w-5 h-5 text-blue-600" />
              <div>
                <p className="font-semibold">Export Your Data</p>
                <p className="text-sm text-slate-600">Download all your personal data in JSON format</p>
              </div>
            </div>
            <Button 
              onClick={handleExportData}
              disabled={exporting}
            >
              {exporting ? 'Exporting...' : 'Export Data'}
            </Button>
          </div>

          <div className="flex items-center justify-between p-4 border rounded-lg border-red-200 bg-red-50">
            <div className="flex items-center gap-3">
              <Trash2 className="w-5 h-5 text-red-600" />
              <div>
                <p className="font-semibold text-red-900">Delete Your Account</p>
                <p className="text-sm text-red-700">Permanently delete your account and all data (1 year retention)</p>
              </div>
            </div>
            <Button 
              variant="destructive"
              onClick={() => setShowDeleteDialog(true)}
            >
              Request Deletion
            </Button>
          </div>
        </CardContent>
      </Card>

      {/* Consent Management */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Cookie className="w-5 h-5" />
            Consent Preferences
          </CardTitle>
          <CardDescription>
            Manage your consent for data processing and cookies
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="flex items-center justify-between p-4 border rounded-lg">
            <div>
              <Label htmlFor="analytics" className="font-semibold">Analytics Cookies</Label>
              <p className="text-sm text-slate-600">Help us improve by collecting anonymous usage data</p>
            </div>
            <Switch
              id="analytics"
              checked={consents.analytics || false}
              onCheckedChange={(checked) => 
                updateConsent('analytics', checked, 'User consent for analytics and usage tracking')
              }
            />
          </div>

          <div className="flex items-center justify-between p-4 border rounded-lg">
            <div>
              <Label htmlFor="marketing" className="font-semibold">Marketing Communications</Label>
              <p className="text-sm text-slate-600">Receive product updates and promotional emails</p>
            </div>
            <Switch
              id="marketing"
              checked={consents.marketing || false}
              onCheckedChange={(checked) => 
                updateConsent('marketing', checked, 'User consent for marketing communications')
              }
            />
          </div>

          <div className="flex items-center justify-between p-4 border rounded-lg">
            <div>
              <Label htmlFor="data-enrichment" className="font-semibold">AI Data Enrichment</Label>
              <p className="text-sm text-slate-600">Use AI to enhance lead and contact information</p>
            </div>
            <Switch
              id="data-enrichment"
              checked={consents.data_enrichment !== false}
              onCheckedChange={(checked) => 
                updateConsent('data_enrichment', checked, 'User consent for AI-powered data enrichment')
              }
            />
          </div>
        </CardContent>
      </Card>

      {/* Legal Documents */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <FileText className="w-5 h-5" />
            Legal Documents
          </CardTitle>
          <CardDescription>
            Review our policies and terms
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-2">
          <Button 
            variant="outline" 
            className="w-full justify-start"
            onClick={() => window.open('/privacy-policy', '_blank')}
          >
            <FileText className="w-4 h-4 mr-2" />
            Privacy Policy
          </Button>
          <Button 
            variant="outline" 
            className="w-full justify-start"
            onClick={() => window.open('/terms', '_blank')}
          >
            <FileText className="w-4 h-4 mr-2" />
            Terms of Service
          </Button>
          <Button 
            variant="outline" 
            className="w-full justify-start"
            onClick={() => window.open('/cookie-policy', '_blank')}
          >
            <Cookie className="w-4 h-4 mr-2" />
            Cookie Policy
          </Button>
        </CardContent>
      </Card>

      {/* Delete Account Dialog */}
      <AlertDialog open={showDeleteDialog} onOpenChange={setShowDeleteDialog}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle className="flex items-center gap-2 text-red-600">
              <AlertTriangle className="w-5 h-5" />
              Delete Your Account?
            </AlertDialogTitle>
            <AlertDialogDescription className="space-y-3">
              <p>This action will:</p>
              <ul className="list-disc pl-6 space-y-1 text-sm">
                <li>Immediately deactivate your account</li>
                <li>Retain your data for 1 year (365 days) for legal and backup purposes</li>
                <li>Permanently anonymize your data after the retention period</li>
                <li>You can cancel this request within the 1-year period</li>
              </ul>
              <div className="mt-4">
                <Label htmlFor="reason">Reason for deletion (optional)</Label>
                <Textarea
                  id="reason"
                  placeholder="Help us understand why you're leaving..."
                  value={deleteReason}
                  onChange={(e) => setDeleteReason(e.target.value)}
                  className="mt-2"
                />
              </div>
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction 
              onClick={handleRequestDeletion}
              className="bg-red-600 hover:bg-red-700"
            >
              Yes, Delete My Account
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
};

export default GDPRSettings;